import React, { useEffect, useState } from 'react';

import { CSVLink } from 'react-csv';

import useQuery from '../../../helpers/JIFI/useQuery';

import SupplyChainNav from '../../../components/supply-chain/nav.js';
import { Col } from '@shadcn/components/ui/col.jsx';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card.jsx';
import { Button } from '@shadcn/components/ui/button.jsx';
import { Input } from '@shadcn/components/ui/input.jsx';
import { TableSkeleton } from '@shadcn/components/ui/table.jsx';
import { localDateOf, todayLocalISO } from './closing.utils';

const REGIONS = ['', 'AU', 'US', 'UK', 'EU'];

// Closing stock-on-hand as at a date, by market: every unit held (on hand, committed,
// display, damaged, quarantine) from the stock ledger, net of sales. Receipts beside it.
// A date is the end of that day in each market's own time zone. The ledger's history
// starts at its opening balances, and the server refuses a date before them.
const ClosingSohList = () => {
  const [asOf, setAsOf] = useState(todayLocalISO);
  const [region, setRegion] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  // Kept once known, so the picker stays bounded while another date loads.
  const [historyStartsAt, setHistoryStartsAt] = useState(null);

  const [report, rowsState, rowsError] = useQuery({
    url: `/stock-receipt/closing`,
    queryData: { asOf, region },
    runQuery: !!asOf,
  });

  useEffect(() => {
    if (report?.historyStartsAt) setHistoryStartsAt(report.historyStartsAt);
  }, [report]);

  const data = Array.isArray(report?.rows) ? report.rows : [];
  const filtered = skuFilter
    ? data.filter((r) => r.sku?.toLowerCase().includes(skuFilter.toLowerCase()))
    : data;

  const csvData = [
    ['Market', 'SKU', `Closing SOH @ ${asOf}`, 'Committed', `Receipts to ${asOf}`],
    ...filtered.map((r) => [r.region, r.sku, r.closingSoh ?? r.qty, r.committed ?? 0, r.cumulativeReceipts ?? '']),
  ];

  const handleSkuKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) setSkuFilter(e.target.value);
  };

  return (
    <SupplyChainNav>
      <div>
        <Col className="w-full">
          <Card className="pt-6 mb-10">
            <CardBody>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4 className="mb-3">Closing Stock-on-Hand</h4>
                <CSVLink
                  style={{ float: 'right', marginBottom: '1.25rem' }}
                  data={csvData}
                  filename={`CLOSING-SOH-${asOf}.csv`}
                >
                  <Button type="button" variant="secondary" className="btn-md btn-rounded">
                    CSV
                  </Button>
                </CSVLink>
              </div>

              <div className="flex gap-4 mb-3">
                <div>
                  <label className="control-label">AS AT DATE</label>
                  <Input
                    type="date"
                    className="form-control"
                    value={asOf}
                    min={historyStartsAt ? localDateOf(historyStartsAt) : undefined}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                </div>
                <div>
                  <label className="control-label">MARKET</label>
                  <select
                    className="form-control"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  >
                    {REGIONS.map((r) => (
                      <option key={r || 'all'} value={r}>
                        {r || 'All markets'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="control-label">FILTER SKU</label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Search SKU…"
                    onKeyDown={handleSkuKeyDown}
                  />
                </div>
              </div>

              <p className="text-muted-foreground">
                Each market closes at the end of the day in its own time zone.
                {historyStartsAt
                  ? ` Ledger history starts ${new Date(historyStartsAt).toLocaleString()}; earlier dates cannot be reported.`
                  : ''}
              </p>

              <div className="table-responsive">
                <table className="table table-centered table-nowrap">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>SKU</th>
                      <th className="text-center">Closing SOH</th>
                      <th className="text-center" title="Picked or sent to a 3PL, not yet shipped. Included in Closing SOH.">
                        Committed
                      </th>
                      <th className="text-center" title="Goods received on or before the date. Not net of sales.">
                        Receipts to date
                      </th>
                    </tr>
                  </thead>
                  {rowsState === 'success' ? (
                    <tbody>
                      {filtered.length > 0 ? (
                        filtered.map((r, key) => (
                          <tr key={`${r.region}-${r.sku}-${key}`}>
                            <td>{r.region}</td>
                            <td className="font-weight-bold">{r.sku}</td>
                            <td className="text-center font-weight-bold">{r.closingSoh ?? r.qty}</td>
                            <td className="text-center">{r.committed ?? 0}</td>
                            <td className="text-center">{r.cumulativeReceipts ?? ''}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            No stock held or received on/before {asOf}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ) : rowsState === 'error' ? (
                    <tbody>
                      <tr>
                        {/* A date before the ledger's history is a 400 that says which date to choose. */}
                        <td colSpan={5} className="text-center text-danger">
                          {rowsError || 'Closing stock-on-hand could not be loaded. Try again.'}
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <TableSkeleton columns={5} perPage={10} centerIndex={[2, 3, 4]} />
                  )}
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </div>
    </SupplyChainNav>
  );
};

export default ClosingSohList;
